import { useEffect, useRef, useState } from "react";
import {BrowserRouter, Link, Routes, Route, useLocation} from "react-router-dom";
import ProtectedRoute from "./ProtectedRoute.jsx";
import Home from "./pages/Home.jsx";
import Login from "./pages/Login.jsx";
import Banks from "./pages/Banks.jsx";
import BanksAdd from "./pages/BanksAdd.jsx";
import BanksUpdate from "./pages/BanksUpdate.jsx";
import BankBalances from "./pages/BankBalances.jsx";
import Loans from "./pages/Loans.jsx";
import LoansAdd from "./pages/LoansAdd.jsx";
import LoansUpdate from "./pages/LoansUpdate.jsx";
import LoanBalances from "./pages/LoanBalances.jsx";
import Categories from "./pages/Categories.jsx";
import CategoriesAdd from "./pages/CategoriesAdd.jsx";
import CategoriesUpdate from "./pages/CategoriesUpdate.jsx";
import Currencies from "./pages/Currencies.jsx";
import CurrenciesAdd from "./pages/CurrenciesAdd.jsx";
import CurrenciesUpdate from "./pages/CurrenciesUpdate.jsx";
import Periods from "./pages/Periods.jsx";
import PeriodsAdd from "./pages/PeriodsAdd.jsx";
import PeriodsUpdate from "./pages/PeriodsUpdate.jsx";
import Subcategories from "./pages/Subcategories.jsx";
import SubcategoriesAdd from "./pages/SubcategoriesAdd.jsx";
import SubcategoriesUpdate from "./pages/SubcategoriesUpdate.jsx";
import TransactionTypes from "./pages/TransactionTypes.jsx";
import TransactionTypesAdd from "./pages/TransactionTypesAdd.jsx";
import TransactionTypesUpdate from "./pages/TransactionTypesUpdate.jsx";
import TransactionsAdd from "./pages/TransactionsAdd.jsx";
import Transactions from "./pages/Transactions.jsx";
import TransactionsUpdate from "./pages/TransactionsUpdate.jsx";
import Assets from "./pages/Assets.jsx";
import AssetsAdd from "./pages/AssetsAdd.jsx";
import AssetsUpdate from "./pages/AssetsUpdate.jsx";
import Reports from "./pages/Reports.jsx";
import ReportsIncomeExpenditureTracking from "./pages/ReportsIncomeExpenditureTracking.jsx";
import ReportsIncomeTrends from "./pages/ReportsIncomeTrends.jsx";
import ReportsNetWorth from "./pages/ReportsNetWorth.jsx";
import ReportsPassiveIncome from "./pages/ReportsPassiveIncome.jsx";
import ReportsPassiveIncomeTracking from "./pages/ReportsPassiveIncomeTracking.jsx";
import NetWorth from "./pages/NetWorth.jsx";
import ReportsPeriodSummary from "./pages/ReportsPeriodSummary.jsx";
import ReportsPeriodTransactions from "./pages/ReportsPeriodTransactions.jsx";
import ReportsSpendingTrends from "./pages/ReportsSpendingTrends.jsx";
import ReportsDashboard from "./pages/ReportsDashboard.jsx";
import Entities from "./pages/Entities.jsx";
import Settings from "./pages/Settings.jsx";
import GettingStarted from "./pages/GettingStarted.jsx";
import Backups from "./pages/Backups.jsx";
import { clearAuthSession, getAuthSession, isAuthenticated, setActiveEntityId, subscribeToAuthSession } from "./auth.js";
import "./style.css"

const THEME_STORAGE_KEY = "findb-theme";

const primaryNavLinks = [
  { to: "/", label: "Home", matches: ["/"] },
  { to: "/Transactions", label: "Transactions", matches: ["/Transactions", "/TransactionsAdd", "/TransactionsUpdate"] },
  { to: "/Assets", label: "Assets", matches: ["/Assets", "/AssetsAdd", "/AssetsUpdate"] },
  { to: "/Banks", label: "Banks", matches: ["/Banks", "/BanksAdd", "/BanksUpdate", "/BankBalances"] },
  {
    to: "/Reports",
    label: "Reports",
    matches: [
      "/Reports",
      "/ReportsPassiveIncome",
      "/ReportsPassiveIncomeTracking",
      "/ReportsNetWorth",
      "/ReportsDashboard",
      "/ReportsIncomeExpenditureTracking",
      "/ReportsIncomeTrends",
      "/ReportsPeriodSummary",
      "/ReportsPeriodTransactions",
      "/ReportsSpendingTrends",
    ],
  },
  { to: "/Settings", label: "Settings", matches: ["/Settings"] },
];

const moreNavLinks = [
  { to: "/NetWorth", label: "Net Worth" },
  { to: "/Loans", label: "Loans" },
  { to: "/Periods", label: "Periods" },
  { to: "/Currencies", label: "Currencies" },
  { to: "/Categories", label: "Categories" },
  { to: "/Subcategories", label: "Subcategories" },
  { to: "/TransactionTypes", label: "Transaction Types" },
  { to: "/Entities", label: "Entities" },
  { to: "/Backups", label: "Backups" },
  { to: "/GettingStarted", label: "Getting Started" },
];

function getInitialTheme() {
  if (typeof window === "undefined") {
    return "light";
  }

  const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
  return storedTheme === "dark" ? "dark" : "light";
}

function AppHeader({ theme, onToggleTheme }) {
  const location = useLocation();
  const [session, setSession] = useState(() => getAuthSession());
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [entityMenuOpen, setEntityMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const moreMenuRef = useRef(null);
  const entityMenuRef = useRef(null);

  useEffect(() => subscribeToAuthSession(() => setSession(getAuthSession())), []);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!userMenuRef.current?.contains(event.target)) {
        setUserMenuOpen(false);
      }
      if (!moreMenuRef.current?.contains(event.target)) {
        setMoreMenuOpen(false);
      }
      if (!entityMenuRef.current?.contains(event.target)) {
        setEntityMenuOpen(false);
      }
    };

    window.addEventListener("pointerdown", handlePointerDown);
    return () => window.removeEventListener("pointerdown", handlePointerDown);
  }, []);

  if (location.pathname === "/Login" || !isAuthenticated()) {
    return null;
  }

  const activeEntity = session?.entities?.find((item) => Number(item.EntityID) === Number(session?.entityId));
  const entityLabel = activeEntity?.Description || `Entity ${session?.entityId || ""}`;
  const userLabel = session?.user?.UserName || "Unknown user";
  const isMoreActive = moreNavLinks.some((link) => location.pathname === link.to);
  const availableEntities = Array.isArray(session?.entities) ? session.entities : [];

  const handleEntitySelection = (entityId) => {
    if (!entityId || Number(entityId) === Number(session?.entityId)) {
      setEntityMenuOpen(false);
      return;
    }
    setActiveEntityId(entityId);
    setEntityMenuOpen(false);
    window.location.href = `${location.pathname}${location.search}${location.hash}`;
  };

  return (
    <header className="app-header-shell">
      <div className="app-header">
        <div className="app-brand-block">
          <nav className="primary-nav" aria-label="Primary">
            {primaryNavLinks.map((link) => {
              const active = link.matches.includes(location.pathname);
              return (
                <Link
                  key={link.to}
                  className={`primary-nav-link${active ? " primary-nav-link-active" : ""}`}
                  to={link.to}
                >
                  {link.label}
                </Link>
              );
            })}
            <div className="header-menu" ref={moreMenuRef}>
              <button
                className={`primary-nav-link primary-nav-button${isMoreActive || moreMenuOpen ? " primary-nav-link-active" : ""}`}
                type="button"
                onClick={() => setMoreMenuOpen((prev) => !prev)}
              >
                More
              </button>
              {moreMenuOpen ? (
                <div className="header-menu-popup">
                  {moreNavLinks.map((link) => (
                    <Link
                      key={link.to}
                      className="header-menu-item"
                      to={link.to}
                      onClick={() => setMoreMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              ) : null}
            </div>
          </nav>
        </div>
        <div className="session-indicator-meta">
          <button
            className="theme-toggle"
            type="button"
            onClick={onToggleTheme}
            aria-pressed={theme === "dark"}
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          >
            {theme === "dark" ? "Light mode" : "Dark mode"}
          </button>
          <div className="session-user-menu" ref={entityMenuRef}>
            <button
              className="session-user-button"
              type="button"
              onClick={() => setEntityMenuOpen((prev) => !prev)}
            >
              <span className="session-indicator-label">Entity</span> {entityLabel}
            </button>
            {entityMenuOpen ? (
              <div className="session-user-popup">
                {availableEntities.map((item) => {
                  const selected = Number(item.EntityID) === Number(session?.entityId);
                  return (
                    <button
                      key={item.EntityID}
                      className={`header-menu-item${selected ? " session-menu-item-active" : ""}`}
                      type="button"
                      onClick={() => handleEntitySelection(item.EntityID)}
                    >
                      {item.Description}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
          <div className="session-user-menu" ref={userMenuRef}>
            <button
              className="session-user-button"
              type="button"
              onClick={() => setUserMenuOpen((prev) => !prev)}
            >
              <span className="session-indicator-label">User</span> {userLabel}
            </button>
            {userMenuOpen ? (
              <div className="session-user-popup">
                <button
                  className="session-user-popup-action"
                  type="button"
                  onClick={() => {
                    clearAuthSession();
                    window.location.href = "/Login";
                  }}
                >
                  Log Out
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </header>
  );
}

function App() {
  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  return (
    <div className="app">
      <BrowserRouter>
        <AppHeader
          theme={theme}
          onToggleTheme={() => setTheme((currentTheme) => currentTheme === "dark" ? "light" : "dark")}
        />
        <Routes>
          <Route path="/Login" element={<Login />}/>
          <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>}/>
          <Route path="/Banks" element={<ProtectedRoute><Banks /></ProtectedRoute>}/>
          <Route path="/BanksAdd" element={<ProtectedRoute><BanksAdd /></ProtectedRoute>}/>
          <Route path="/BanksUpdate/:BankId" element={<ProtectedRoute><BanksUpdate /></ProtectedRoute>}/>
          <Route path="/BankBalances" element={<ProtectedRoute><BankBalances /></ProtectedRoute>}/>
          <Route path="/Loans" element={<ProtectedRoute><Loans /></ProtectedRoute>}/>
          <Route path="/LoansAdd" element={<ProtectedRoute><LoansAdd /></ProtectedRoute>}/>
          <Route path="/LoansUpdate/:LoanId" element={<ProtectedRoute><LoansUpdate /></ProtectedRoute>}/>
          <Route path="/LoanBalances" element={<ProtectedRoute><LoanBalances /></ProtectedRoute>}/>
          <Route path="/Categories" element={<ProtectedRoute><Categories /></ProtectedRoute>}/>
          <Route path="/CategoriesAdd" element={<ProtectedRoute><CategoriesAdd /></ProtectedRoute>}/>
          <Route path="/CategoriesUpdate/:CategoryId" element={<ProtectedRoute><CategoriesUpdate /></ProtectedRoute>}/>
          <Route path="/Currencies" element={<ProtectedRoute><Currencies /></ProtectedRoute>}/>
          <Route path="/CurrencyAdd" element={<ProtectedRoute><CurrenciesAdd /></ProtectedRoute>}/>
          <Route path="/CurrenciesUpdate/:CurrencyId" element={<ProtectedRoute><CurrenciesUpdate /></ProtectedRoute>}/>
          <Route path="/Periods" element={<ProtectedRoute><Periods /></ProtectedRoute>}/>
          <Route path="/PeriodsAdd" element={<ProtectedRoute><PeriodsAdd /></ProtectedRoute>}/>
          <Route path="/PeriodsUpdate/:PeriodId" element={<ProtectedRoute><PeriodsUpdate /></ProtectedRoute>}/>
          <Route path="/TransactionTypes" element={<ProtectedRoute><TransactionTypes /></ProtectedRoute>}/>
          <Route path="/TransactionTypesAdd" element={<ProtectedRoute><TransactionTypesAdd /></ProtectedRoute>}/>
          <Route path="/TransactionTypesUpdate/:TransactionTypeId" element={<ProtectedRoute><TransactionTypesUpdate /></ProtectedRoute>}/>
          <Route path="/TransactionsAdd" element={<ProtectedRoute><TransactionsAdd /></ProtectedRoute>}/>
          <Route path="/Transactions" element={<ProtectedRoute><Transactions /></ProtectedRoute>}/>
          <Route path="/TransactionsUpdate/:TransactionId" element={<ProtectedRoute><TransactionsUpdate /></ProtectedRoute>}/>
          <Route path="/Assets" element={<ProtectedRoute><Assets /></ProtectedRoute>}/>
          <Route path="/AssetsAdd" element={<ProtectedRoute><AssetsAdd /></ProtectedRoute>}/>
          <Route path="/AssetsUpdate/:AssetId" element={<ProtectedRoute><AssetsUpdate /></ProtectedRoute>}/>
          <Route path="/Reports" element={<ProtectedRoute><Reports /></ProtectedRoute>}/>
          <Route path="/NetWorth" element={<ProtectedRoute><NetWorth /></ProtectedRoute>}/>
          <Route path="/ReportsIncomeExpenditureTracking" element={<ProtectedRoute><ReportsIncomeExpenditureTracking /></ProtectedRoute>}/>
          <Route path="/ReportsIncomeTrends" element={<ProtectedRoute><ReportsIncomeTrends /></ProtectedRoute>}/>
          <Route path="/ReportsNetWorth" element={<ProtectedRoute><ReportsNetWorth /></ProtectedRoute>}/>
          <Route path="/ReportsDashboard" element={<ProtectedRoute><ReportsDashboard /></ProtectedRoute>}/>
          <Route path="/ReportsPassiveIncome" element={<ProtectedRoute><ReportsPassiveIncome /></ProtectedRoute>}/>
          <Route path="/ReportsPassiveIncomeTracking" element={<ProtectedRoute><ReportsPassiveIncomeTracking /></ProtectedRoute>}/>
          <Route path="/ReportsPeriodSummary" element={<ProtectedRoute><ReportsPeriodSummary /></ProtectedRoute>}/>
          <Route path="/ReportsPeriodTransactions" element={<ProtectedRoute><ReportsPeriodTransactions /></ProtectedRoute>}/>
          <Route path="/ReportsSpendingTrends" element={<ProtectedRoute><ReportsSpendingTrends /></ProtectedRoute>}/>
          <Route path="/Entities" element={<ProtectedRoute><Entities /></ProtectedRoute>}/>
          <Route path="/Settings" element={<ProtectedRoute><Settings /></ProtectedRoute>}/>
          <Route path="/Backups" element={<ProtectedRoute><Backups /></ProtectedRoute>}/>
          <Route path="/GettingStarted" element={<ProtectedRoute><GettingStarted /></ProtectedRoute>}/>
          <Route path="/Subcategories" element={<ProtectedRoute><Subcategories /></ProtectedRoute>}/>
          <Route path="/SubcategoriesAdd" element={<ProtectedRoute><SubcategoriesAdd /></ProtectedRoute>}/>
          <Route path="/SubcategoriesUpdate/:SubcategoryId" element={<ProtectedRoute><SubcategoriesUpdate /></ProtectedRoute>}/>
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
